import { RunDto } from "@custom-types/modelRun"
import { GraphNodeType } from "@custom-types/graphs"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { DashboardBarStringsChart } from "@features/results/Result/Dashboard/DashboardBarStringsChart"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import {
  ChartGroupLayout,
  DashboardBarCategoricalChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { calculateLagShift } from "@utils/run"
import { useGetRunsNodesResultsHistoricAggregateQuery } from "@store/services/runService"
import React, { useMemo, useRef } from "react"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

export const DashboardBarCategoricalChart = (props: {
  chart: DashboardBarCategoricalChartDto
  runs: RunDto[]
  layout?: ChartGroupLayout
}) => {
  const runKeys = props.runs.map((run) => run.key)
  const categoricalResetZoomRef = useRef<() => void>(() => {})

  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    selectedNodeIndex: 0,
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = useMemo(() => cfg.selectedNodes ?? [], [cfg.selectedNodes])

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[], index: number) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
      draft.selectedNodeIndex = index
    })
  }

  const handleShowAsTableToggle = (newValue: boolean) => {
    updateCfg((draft) => {
      draft.showAsTable = newValue
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const { isFetching, data: aggregateData } = useGetRunsNodesResultsHistoricAggregateQuery(
    {
      runKeys,
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: selectedNodes.length === 0 || !isVisible,
    },
  )

  const lagShift = useMemo(
    () => calculateLagShift(aggregateData?.results),
    [aggregateData?.results],
  )

  const nodeLabelsForCategorical: GroupNodeLabel[] = useMemo(() => {
    return selectedNodes.map((label) => ({
      label,
      key: graphViewGroupNodeLabelToSampleNode(label),
      type: GraphNodeType.CATEGORICAL,
      group: "",
      sort: "",
    }))
  }, [selectedNodes])

  // for compare results chart "Stacked" and "ShowAsPercentage" are static as other values do not make sense
  const isStacked = !props.chart.isCompareResultsChart ? props.chart.Stacked ?? true : false
  const showAsPercentage = !props.chart.isCompareResultsChart
    ? props.chart.ShowAsPercentage ?? true
    : false

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={(value, idx) => handleOptionChange(value.Nodes, idx)}
      onFullScreen={handleFullscreen}
      onResetZoom={() => categoricalResetZoomRef.current()}
      showAsTable={cfg.showAsTable}
      onShowAsTableToggle={handleShowAsTableToggle}
      runIds={runKeys}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isVisible && (
        <DashboardBarStringsChart
          aggregateData={aggregateData}
          nodes={nodeLabelsForCategorical}
          lagShift={lagShift}
          isLoading={isFetching}
          runs={props.runs}
          isStacked={isStacked}
          showAsPercentage={showAsPercentage}
          showAsTable={cfg.showAsTable}
          showLegend={cfg.showLegend}
          hiddenDatasets={cfg.hiddenDatasets}
          onLegendClick={handleLegendClick}
          legendPosition={props.chart.LegendPosition}
          onResetZoomReady={(handler) => {
            categoricalResetZoomRef.current = handler
          }}
        />
      )}
    </DashboardChartContainer>
  )
}
